// Rahmen: Header ein-/ausblenden, mobiles Menü, Ankündigungs-Wechsel, weicher Bildtausch auf der Produktseite.
// Animiert nur transform/opacity per CSS; bei reduzierter Bewegung ohne Übergänge.
const motion = matchMedia('(prefers-reduced-motion: reduce)');
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, motion.matches ? 0 : ms));

/* Header: beim Runterscrollen aus, beim Hochscrollen ein */
function initHeader() {
  const header = document.querySelector('[data-tg-header]');
  const section = header?.closest('.shopify-section');
  if (!section || section.dataset.tgReady) return;
  section.dataset.tgReady = '';

  // Horizon scrollt ab 990 px in .page-wrapper statt im Fenster
  const wrapper = document.querySelector('.page-wrapper');
  const scrollTop = () => Math.max(window.scrollY, wrapper?.scrollTop || 0);
  let lastY = scrollTop();
  let ticking = false;
  const setHidden = (hidden) => section.toggleAttribute('data-tg-hidden', hidden);

  const update = () => {
    ticking = false;
    const y = scrollTop();
    const delta = y - lastY;
    if (Math.abs(delta) < 6) return;
    const busy = section.contains(document.activeElement) || document.querySelector('dialog[open]');
    setHidden(!busy && delta > 0 && y > section.offsetHeight * 2);
    lastY = y;
  };

  document.addEventListener(
    'scroll',
    () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    },
    { passive: true, capture: true }
  );
  section.addEventListener('focusin', () => setHidden(false));
}

/* Mobiles Menü: natives <dialog> hält den Fokus, Esc löst "cancel" aus */
function initMenu() {
  const dialog = document.querySelector('[data-tg-menu]');
  const opener = document.querySelector('[data-tg-menu-open]');
  if (!dialog || !opener || dialog.dataset.tgReady) return;
  dialog.dataset.tgReady = '';

  const open = () => {
    dialog.showModal();
    opener.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => dialog.classList.add('is-open'));
  };
  const close = async () => {
    if (!dialog.open) return;
    dialog.classList.remove('is-open');
    await wait(220);
    dialog.close();
  };

  opener.addEventListener('click', open);
  dialog.querySelector('[data-tg-menu-close]')?.addEventListener('click', close);
  dialog.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });
  dialog.addEventListener('close', () => {
    dialog.classList.remove('is-open');
    opener.setAttribute('aria-expanded', 'false');
    opener.focus();
  });
  // Fokus im Menü halten (der native Dialog lässt Tab sonst in die Browserleiste)
  dialog.addEventListener('keydown', (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...dialog.querySelectorAll('a[href], button:not([disabled])')];
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });
  dialog.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('a[href]')) close();
  });
  matchMedia('(min-width: 1100px)').addEventListener('change', (event) => {
    if (event.matches) close();
  });
}

/* Ankündigungen: mobil weicher Wechsel, pausiert bei Hover/Fokus */
function initAnnouncements() {
  const bar = document.querySelector('[data-tg-rotate]');
  if (!bar || bar.dataset.tgReady) return;
  bar.dataset.tgReady = '';
  const items = [...bar.querySelectorAll('.tg-announcement__item')];
  const mobile = matchMedia('(max-width: 749px)');
  let index = 0;
  let paused = false;

  bar.addEventListener('pointerenter', () => (paused = true));
  bar.addEventListener('pointerleave', () => (paused = false));
  bar.addEventListener('focusin', () => (paused = true));
  bar.addEventListener('focusout', () => (paused = false));

  setInterval(() => {
    if (paused || !mobile.matches || motion.matches || document.hidden) return;
    items[index].classList.remove('is-active');
    index = (index + 1) % items.length;
    items[index].classList.add('is-active');
  }, 4500);
}

/* Produktseite: Bildtausch bei Variantenwechsel weich einblenden (Horizon tauscht per DOM-Morph) */
function initMediaSwap() {
  // Horizon ersetzt die Galerie beim Variantenwechsel komplett – daher die ganze Section beobachten
  const section = document.querySelector('.product-information')?.closest('.shopify-section');
  if (!section || section.dataset.tgSwapReady || motion.matches) return;
  section.dataset.tgSwapReady = '';
  let timer;
  let ready = false;
  setTimeout(() => (ready = true), 1500); // Hydrierung beim Laden ignorieren
  new MutationObserver((records) => {
    if (!ready || !records.some((r) => r.target.closest?.('media-gallery') || [...r.addedNodes].some((n) => n.nodeType === 1 && (n.matches('media-gallery') || n.querySelector?.('media-gallery'))))) return;
    const gallery = section.querySelector('media-gallery');
    if (!gallery) return;
    gallery.classList.remove('tg-media-swap');
    void gallery.offsetWidth;
    gallery.classList.add('tg-media-swap');
    clearTimeout(timer);
    timer = setTimeout(() => gallery.classList.remove('tg-media-swap'), 500);
  }).observe(section, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset'] });
}

/* Fokus in offenen Modal-Dialogen halten (Filter-Drawer, Suche, Warenkorb); nativ verlässt Tab sonst den Dialog.
   Dazu: Esc schließt den Drawer auch, wenn der Fokus auf der Sortier-Auswahl steht. */
function initDialogFocusTrap() {
  if (document.documentElement.dataset.tgTrap) return;
  document.documentElement.dataset.tgTrap = '';
  document.addEventListener('keydown', (event) => {
    // Chrome markiert Esc auf einem nativen <select> als behandelt; der Drawer ignoriert es dann und bliebe offen
    if (event.key === 'Escape' && event.target instanceof HTMLSelectElement) {
      const drawer = event.target.closest('theme-drawer');
      if (drawer?.isOpen) drawer.close();
      return;
    }
    if (event.key !== 'Tab') return;
    const dialog = [...document.querySelectorAll('dialog[open]')].reverse().find((d) => d.matches(':modal'));
    if (!dialog || dialog.matches('[data-tg-menu]')) return;
    // Alle per Tab erreichbaren Elemente, auch Express-Buttons (Custom Elements, iframes)
    const focusable = [...dialog.querySelectorAll('*')].filter((el) => el.tabIndex >= 0 && !el.disabled && !el.closest('[inert], [hidden]') && el.getClientRects().length);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable.at(-1);
    const active = document.activeElement;
    if (!dialog.contains(active)) {
      event.preventDefault();
      first.focus();
      return;
    }
    // Position statt Listenvergleich: Express-Buttons (Shadow DOM) stehen nicht in der Liste
    const follows = (el) => active !== el && !active.contains(el) && active.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING;
    if (event.shiftKey && (active === first || !focusable.some((el) => el !== active && !follows(el) && !el.contains(active)))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && !focusable.some(follows)) {
      event.preventDefault();
      first.focus();
    }
  });
}

/* Kollektion: Produktanzahl im Kopf folgt den Filtern (Horizon aktualisiert nur seine Statuszeile im Raster) */
function initCountSync() {
  const count = document.querySelector('[data-tg-count]');
  const main = document.querySelector('.product-grid-container')?.closest('.shopify-section');
  if (!count || !main || main.dataset.tgCount) return;
  main.dataset.tgCount = '';
  const update = () => {
    const n = parseInt(main.querySelector('.products-count-wrapper [role="status"]')?.textContent.replace(/\D/g, ''), 10);
    if (Number.isNaN(n)) return;
    const text = `${n} ${n === 1 ? 'Produkt' : 'Produkte'}`;
    if (count.textContent.trim() !== text) count.textContent = text;
  };
  new MutationObserver(update).observe(main, { subtree: true, childList: true, characterData: true });
}

/* Warenkorb: Änderungen (Hinzufügen, Menge, Entfernen) für Screenreader ansagen.
   Liest nach Horizons Neuaufbau Anzahl und Summe aus dem DOM; der Live-Bereich liegt außerhalb der Sections. */
function initCartLive() {
  const root = document.documentElement;
  if (root.dataset.tgCartLive !== undefined) return;
  root.dataset.tgCartLive = '';
  const live = document.createElement('p');
  live.className = 'visually-hidden';
  live.setAttribute('role', 'status');
  document.body.append(live);
  const read = () => {
    const items = document.querySelector('cart-items-component');
    if (!items) return '';
    if (items.querySelector('[data-cart-drawer-empty], .cart-page--empty') || !items.querySelector('[ref="cartItemRows[]"]')) return 'Ihr Warenkorb ist leer';
    const count = items.querySelector('[ref="cartItemCount"]')?.textContent.trim();
    const total = items.querySelector('[data-cart-subtotal]')?.textContent.trim();
    return `Warenkorb aktualisiert: ${count} Artikel, Zwischensumme ${total}`;
  };
  let last = read();
  let timer;
  new MutationObserver(() => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      const message = read();
      if (message && message !== last) live.textContent = message;
      last = message;
    }, 600);
  }).observe(document.body, { subtree: true, childList: true, characterData: true, attributeFilter: ['data-cart-drawer-empty'] });
}

/* Warenkorb-Seite mobil: Sticky-Leiste ausblenden, sobald "Zur Kasse" der Zusammenfassung erreicht ist */
function initCartBar() {
  if (!document.querySelector('[data-tg-cart-bar]') || document.documentElement.dataset.tgCartBarInit !== undefined) return;
  document.documentElement.dataset.tgCartBarInit = '';
  const observer = new IntersectionObserver(([entry]) => {
    document.documentElement.classList.toggle('tg-cart-ctas-reached', entry.isIntersecting || entry.boundingClientRect.top < 0);
  });
  const watch = () => {
    observer.disconnect();
    const target = document.querySelector('.cart-page__summary .cart__ctas');
    if (target) observer.observe(target);
  };
  watch();
  new MutationObserver(watch).observe(document.querySelector('cart-items-component') || document.body, { childList: true });
}

function init() {
  initHeader();
  initMenu();
  initAnnouncements();
  initMediaSwap();
  initDialogFocusTrap();
  initCountSync();
  initCartLive();
  initCartBar();
}

init();
document.addEventListener('shopify:section:load', init);
